import classnames from 'classnames';
import { diffLines } from 'diff';
import _ from 'lodash';
import React, { CSSProperties, useMemo } from 'react';
import { PrismAsyncLight as Prism } from 'react-syntax-highlighter';

import { prismStudioThemeLight } from '../graphqlCodeBlock/prismStudioThemeLight';

import {
  InlineTextDiff,
  LineNumberPropsFunction,
  LineType,
} from './diffViewHelpers';
import { InlineGraphQLDiffViewLineRenderer } from './graphQLDiffViewLineRenderer/GraphQLDiffViewLineRenderer';

export const InlineGraphQLDiffView = ({
  oldSdl,
  newSdl,
  lineNumberClassName,
  className,
  lineProps,
  onMount,
}: {
  oldSdl: string | undefined;
  newSdl: string | undefined;
  lineNumberClassName?: string;
  className?: string;
  lineProps?: LineNumberPropsFunction | ReturnType<LineNumberPropsFunction>;
  onMount?: () => void;
}) => {
  const textDiff = useMemo(() => {
    let currOriginalLineNumber = 1;
    let currModifiedLineNumber = 1;
    return diffLines(oldSdl ?? '', newSdl ?? '').reduce(
      (acc: InlineTextDiff, line) => {
        line.value
          .replace(/\n$/, '')
          .split('\n')
          .forEach((singleLine) => {
            acc.diffLines.push(singleLine);
            // Account for mismatched line numbers before adding unchanged lines
            if (!line.removed) {
              acc.modifiedDiffLineNumbers.push(currModifiedLineNumber++);
              if (line.added) {
                acc.lineTypes.push(LineType.ADDED);
                acc.originalDiffLineNumbers.push(undefined);
              }
            }
            if (!line.added) {
              acc.originalDiffLineNumbers.push(currOriginalLineNumber++);
              if (line.removed) {
                acc.lineTypes.push(LineType.REMOVED);
                acc.modifiedDiffLineNumbers.push(undefined);
              }
            }
            if (!line.added && !line.removed)
              acc.lineTypes.push(LineType.UNCHANGED);
          });
        return acc;
      },
      {
        diffLines: [],
        originalDiffLineNumbers: [],
        modifiedDiffLineNumbers: [],
        lineTypes: [],
      },
    );
  }, [oldSdl, newSdl]);

  return (
    <div className={classnames('overflow-auto text-xs', className)}>
      <Prism
        language="graphql"
        style={prismStudioThemeLight}
        PreTag={({
          style,
          ...props
        }: {
          className?: string;
          style: CSSProperties;
        }) => (
          <pre
            {...props}
            className={props.className}
            style={{
              ..._.omit(
                style,
                'fontSize',
                'fontFamily',
                'padding',
                'margin',
                'background',
              ),
              flex: '1 1 0%',
            }}
          />
        )}
        renderer={({ rows, stylesheet, useInlineStyles }) =>
          rows.map((node, i) => (
            <InlineGraphQLDiffViewLineRenderer
              node={node}
              stylesheet={stylesheet}
              useInlineStyles={useInlineStyles}
              lineNumberClassName={lineNumberClassName}
              index={i}
              textDiff={textDiff}
              lineType={textDiff.lineTypes[i] ?? LineType.UNCHANGED}
              // lineNumberClassName="hidden"
              lineProps={lineProps}
              // eslint-disable-next-line react/no-array-index-key
              key={`code-segment-${i}`}
              onMount={onMount}
            />
          ))
        }
      >
        {textDiff.diffLines.join('\n')}
      </Prism>
    </div>
  );
};
